import React, { useState, useEffect, useRef } from "react";
import { DayPicker } from "react-day-picker";
import { User, Search } from "lucide-react";
import {
  Offcanvas,
  Button,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../FireBase";
import "react-day-picker/dist/style.css";
import Skeleton from "react-loading-skeleton";

const MOBILE_BREAKPOINT = 768;
const STORAGE_KEY = "bookingSearch";

function formatRange(range) {
  if (!range.from || !range.to) return "";
  return `${range.from.toLocaleDateString()} → ${range.to.toLocaleDateString()}`;
}

export default function ImageSlider() {
  const [sliderImages, setSliderImages] = useState([]);
  const [currentImage, setCurrentImage] = useState(0);
  const [range, setRange] = useState({ from: undefined, to: undefined });

  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState([]); // array of ages

  const [showCalendar, setShowCalendar] = useState(false);
  const [showGuestBox, setShowGuestBox] = useState(false);
  const [showMobileOffcanvas, setShowMobileOffcanvas] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  const calendarRef = useRef(null);
  const guestRef = useRef(null);
  const navigate = useNavigate();
  const [imgLoaded, setImgLoaded] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const [loadedImages, setLoadedImages] = useState([]);
  const [loading, setLoading] = useState(true);

    useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // hydrate saved search data
useEffect(() => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (!saved) return;

  try {
    const { from, to, adults: a, children: c } = JSON.parse(saved);

    setRange({
      from: from ? new Date(from) : undefined,
      to: to ? new Date(to) : undefined,
    });

    if (typeof a === "number" && a >= 1) setAdults(a);

    if (Array.isArray(c)) {
      setChildren(c);
    } else if (typeof c === "number") {
      // fallback for old storage structure
      setChildren(new Array(c).fill(""));
    }
  } catch (err) {
    console.error("Failed to parse saved bookingSearch:", err);
  }
}, []);

  // add/remove/update child
  const addChild = () => setChildren([...children, ""]);
  const removeChild = () => setChildren(children.slice(0, -1));
  const updateChildAge = (index, value) => {
    const updated = [...children];
    updated[index] = value;
    setChildren(updated);
  };

  // preload images
  useEffect(() => {
    const cachedImages = localStorage.getItem("sliderImages");
    if (cachedImages) {
      const parsed = JSON.parse(cachedImages);
      setSliderImages(parsed);
      setLoadedImages(parsed);
      setLoading(false);
    }

    const fetchImages = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "slider_images"));
        const imageUrls = querySnapshot.docs.map((doc) => doc.data().imageUrl);
        setSliderImages(imageUrls);
        setLoadedImages(imageUrls);
        setLoading(false);
        localStorage.setItem("sliderImages", JSON.stringify(imageUrls));
      } catch (error) {
        console.error("Error fetching images:", error);
      }
    };

    fetchImages();
  }, []);

  // auto change slider
  useEffect(() => {
    if (sliderImages.length === 0) return;
    const timer = setInterval(() => {
      setImgLoaded(false);
      setCurrentImage((prev) => (prev + 1) % sliderImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [sliderImages]);

  // click outside calendar
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(e.target) &&
        e.target.getAttribute("data-calendar-field") !== "true"
      ) {
        setShowCalendar(false);
      }
    };
    if (showCalendar && !isMobile) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCalendar, isMobile]);

  // click outside guest box
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        guestRef.current &&
        !guestRef.current.contains(e.target) &&
        e.target.getAttribute("data-guest-field") !== "true"
      ) {
        setShowGuestBox(false);
      }
    };
    if (showGuestBox) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showGuestBox]);

  const handleSearch = (e) => {
    e?.preventDefault();
    if (!range.from || !range.to) {
      alert("Please select a date range.");
      return;
    }
    const payload = {
      from: range.from.toISOString(),
      to: range.to.toISOString(),
      adults,
       children, // array of ages

      guestCount: adults + children.length,
    };
    console.log(payload);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    navigate("/rooms");
    setShowCalendar(false);
    setShowGuestBox(false);
    setShowMobileOffcanvas(false);
  };

  if (loading) {
    return (
      <div className="slider-container">
        <Skeleton
          height={isMobile ? 220 : 350}
          style={{ marginBottom: "1rem", borderRadius: "30px" }}
        />
      </div>
    );
  }

  return (
    <div className="position-relative mb-5">
      <div
        className="rounded-4 overflow-hidden position-relative"
        style={{ minHeight: isMobile ? 220 : 350 }}
      >
        {loadedImages.length > 0 && (
          <img
            src={loadedImages[currentImage]}
            alt="slider"
            onLoad={() => setImgLoaded(true)}
            style={{
              width: "100%",
              height: isMobile ? 220 : 350,
              objectFit: "cover",
              transition: "opacity 0.6s",
            }}
          />
        )}
      </div>

      {/* Desktop Search Bar */}
      {!isMobile && (
        <Form
          onSubmit={handleSearch}
          style={{
            position: "absolute",
            bottom: -50,
            left: "50%",
            transform: "translateX(-50%)",
            width: "60%",
            maxWidth: 800,
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            padding: 24,
            zIndex: 20,
          }}
        >
          <Row className="align-items-end gx-3">
            {/* Dates */}
            <Col md={6}>
              <Form.Label className="fw-semibold mb-1">Select Dates</Form.Label>
              <div
                className={`d-flex align-items-center px-3 py-2 rounded border ${
                  showCalendar ? "border-primary" : "border-secondary"
                }`}
                style={{ cursor: "pointer", background: "#f8f9fa" }}
                onClick={() => setShowCalendar(!showCalendar)}
                data-calendar-field="true"
              >
                <Search size={17} className="text-secondary me-2" />
                <span
                  className={range.from ? "text-dark" : "text-muted"}
                  style={{ fontWeight: 500 }}
                >
                  {formatRange(range) || "Select date range"}
                </span>
              </div>
              {showCalendar && (
                <div
                  ref={calendarRef}
                  className="position-absolute bg-white rounded shadow p-3"
                  style={{ top: "110%", zIndex: 30 }}
                >
                  <DayPicker
                    mode="range"
                    selected={range}
                    onSelect={setRange}
                    numberOfMonths={1}
                    defaultMonth={range.from}
                    disabled={{ before: tomorrow }}
                  />
                  <Button
                    size="sm"
                    className="mt-2 float-end btn-own"
                    onClick={() => setShowCalendar(false)}
                  >
                    Done
                  </Button>
                </div>
              )}
            </Col>

            {/* Guests */}
            <Col md={4} className="position-relative">
              <Form.Label className="fw-semibold mb-1">Guests</Form.Label>
              <div
                className={`d-flex align-items-center px-3 py-2 rounded border ${
                  showGuestBox ? "border-primary" : "border-secondary"
                }`}
                style={{ cursor: "pointer", background: "#f8f9fa" }}
                onClick={() => setShowGuestBox(!showGuestBox)}
                data-guest-field="true"
              >
                <User size={17} className="text-secondary me-2" />
                <span className="fw-medium">
                  {adults} Adult{adults > 1 ? "s" : ""}
                  {children.length > 0 ? `, ${children.length} Child` : ""}
                </span>
              </div>

              {showGuestBox && (
                <div
                  ref={guestRef}
                  className="position-absolute bg-white rounded shadow p-3"
                  style={{ top: "110%", zIndex: 30, minWidth: 250 }}
                >
                  {/* Adults */}
                  <div className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2">
                    <span>Adults</span>
                    <div className="d-flex align-items-center gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setAdults(Math.max(1, adults - 1))}
                      >
                        -
                      </Button>
                      <span>{adults}</span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setAdults(adults + 1)}
                      >
                        +
                      </Button>
                    </div>
                  </div>

                  {/* Children */}
                  <div className="border rounded px-3 py-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span>Children</span>
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={removeChild}
                          disabled={children.length === 0}
                        >
                          -
                        </Button>
                        <span>{children.length}</span>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={addChild}
                        >
                          +
                        </Button>
                      </div>
                      
                    </div>
         
                    {children.map((age, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center mb-2"
                      >
                        <span className="me-2">Child {index + 1} Age:</span>
                        <Form.Control
                          type="number"
                          min={0}
                          max={17}
                          value={age}
                          onChange={(e) =>
                            updateChildAge(index, e.target.value)
                          }
                          style={{ width: 80 }}
                        />
                      </div>
                    ))}
                  </div>
                <span style={{ fontSize: 11 }} className="text-muted">
                  Children under 7 stay free,7 t0 12 yrs - 50% of adult price, 12 yrs
                  and above - full adult price
                </span>
                  <Button
                    size="sm"
                    className="mt-3 float-end btn-own"
                    onClick={() => setShowGuestBox(false)}
                  >
                    Done
                  </Button>
                </div>
              )}
            </Col>

            {/* Submit */}
            <Col md={2} className="d-grid">
              <Button type="submit" className="py-2 btn-own">
                <Search size={18} className="me-1" />
                Go
              </Button>
            </Col>
          </Row>
        </Form>
      )}

      {/* Mobile Offcanvas Trigger */}
      {isMobile && (
        <div
          className="d-flex align-items-center gap-3 px-3 py-2 rounded-3 shadow bg-white position-absolute start-50 translate-middle-x w-100"
          style={{ bottom: -28, maxWidth: "98%", cursor: "pointer" }}
          onClick={() => setShowMobileOffcanvas(true)}
        >
          <Search size={17} className="text-secondary" />
          <div>
            <div className="fw-semibold" style={{ fontSize: 13 }}>
              {formatRange(range) || "Select date range"}
            </div>
            <div className="text-secondary" style={{ fontSize: 13 }}>
              · {adults} Adult{adults > 1 ? "s" : ""}
              {children.length > 0 ? `, ${children.length} Child` : ""}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Offcanvas */}
      <Offcanvas
        show={isMobile && showMobileOffcanvas}
        onHide={() => setShowMobileOffcanvas(false)}
        placement="bottom"
        style={{
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          height: "auto",
        }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <Search className="me-2" size={20} />
            Search
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form onSubmit={handleSearch}>
            {/* Dates */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Select Dates</Form.Label>
              <div className="d-flex justify-content-center">
                <DayPicker
                  mode="range"
                  selected={range}
                  onSelect={setRange}
                  numberOfMonths={1}
                  defaultMonth={range.from}
                  disabled={{ before: tomorrow }}
                />
              </div>
            </Form.Group>

            {/* Adults */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Adults</Form.Label>
              <div className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2">
                <span>Adults</span>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setAdults(Math.max(1, adults - 1))}
                  >
                    -
                  </Button>
                  <span>{adults}</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setAdults(adults + 1)}
                  >
                    +
                  </Button>
                </div>
              </div>
            </Form.Group>

            {/* Children */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Children</Form.Label>
              <div className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2">
                <span>Children</span>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={removeChild}
                    disabled={children.length === 0}
                  >
                    -
                  </Button>
                  <span>{children.length}</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={addChild}
                  >
                    +
                  </Button>

                </div>
                
              </div>
              <span style={{ fontSize: 12 }} className="text-muted">
                  Children under 7 stay free,7 t0 12 yrs - 50% of adult price, 12 yrs
                  and above - full adult price
                </span>
              <div style={{ maxHeight: 150, overflowY: 'auto' }}>
              {children.map((age, index) => (
                <div key={index} className="d-flex align-items-center mb-2" >
                  <span className="me-2">Child {index + 1} Age:</span>
                  <Form.Control
                    type="number"
                    min={0}
                    max={17}
                    value={age}
                    onChange={(e) => updateChildAge(index, e.target.value)}
                    style={{ width: 80 }}
                  />
                </div>
              ))}
              </div>
            </Form.Group>

            <Button type="submit" className="w-100 btn-own">
              <Search size={18} className="me-1" />
              Go
            </Button>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}
